import { useCallback } from 'react';
import { useResetRecoilState, useSetRecoilState } from 'recoil';

import { API_URL } from 'constant';
import { useNetwork } from 'hooks';
import { OnboardingCipLoadingState, OnboardingCipState } from 'global-stores';
import { buildCipReport } from '../utils/build-cip-report';

/**
 * Tiered onboarding (Tier-0/Tier-1) CIP export.
 *
 * `fetchCip` loads the normalised, provider-agnostic CIP result (pipelines.api
 * `GET /onboarding-cip/:id`) into `OnboardingCipState` when the session modal opens. `:id` is the
 * selected session id — the endpoint resolves the Tier-0↔Tier-1 pair either way. The display card and
 * the two header downloads (raw JSON / pretty PDF) all read from that single fetch.
 */
export const useOnboardingCip = () => {
	const { get } = useNetwork();
	const setCip = useSetRecoilState(OnboardingCipState);
	const setLoading = useSetRecoilState(OnboardingCipLoadingState);
	const resetCip = useResetRecoilState(OnboardingCipState);

	// Some pipelines services wrap their payload as `{data}`; onboarding services return the object
	// directly. Accept either shape.
	const unwrap = (resp: any) => resp?.data ?? resp ?? null;

	const fetchCip = useCallback(
		async (sessionId?: string) => {
			if (!sessionId) return;
			setLoading(true);
			try {
				const resp = await get(`${API_URL.ONBOARDING_CIP}/${sessionId}`);
				setCip(unwrap(resp));
			} catch {
				setCip(null);
			} finally {
				setLoading(false);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	/** Full provider dump (KYC/AML native payloads) — audit/reconciliation, opt-in. */
	const downloadRaw = useCallback(
		async (sessionId?: string) => {
			if (!sessionId) return;
			const resp = await get(`${API_URL.ONBOARDING_CIP}/${sessionId}?include=raw`);
			const payload = unwrap(resp);
			if (!payload) return;
			const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
			const a = document.createElement('a');
			a.href = window.URL.createObjectURL(blob);
			a.download = `cip-raw-${sessionId}.json`;
			a.dispatchEvent(new MouseEvent('click', { view: window, bubbles: true, cancelable: true }));
			a.remove();
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	/** Pretty, print-ready PDF built from the already-loaded normalised CIP. */
	const downloadFormatted = useCallback((cip: any) => {
		buildCipReport(cip);
	}, []);

	return { fetchCip, downloadRaw, downloadFormatted, resetCip };
};
